import { useEffect, useRef, useState } from "react";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserAvatar } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { LISTING } from "@/components/community/PostCard";

const PLACEHOLDER = { post: "Share an update, a photo from site or news...", question: "Describe the problem: turbine type, error code, what you tried...", listing: "Details: dates, project, price, contact..." };

export default function Composer({ kind = "post", groupId, groups = [], onCreated, listingType }) {
  const api = useApi();
  const { me } = useMe();
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [ltype, setLtype] = useState(listingType || "crew");
  useEffect(() => { if (listingType) setLtype(listingType); }, [listingType]);
  const [group, setGroup] = useState(groupId || "none");
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { setImage({ path: (await api("post", "/community/images", fd)).image_path, preview: URL.createObjectURL(file) }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); fileRef.current.value = ""; }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = await api("post", "/community/posts", { kind, text, title, city, image_path: image?.path || null,
        group_id: group === "none" ? null : group, listing_type: kind === "listing" ? ltype : null });
      onCreated(p); setText(""); setTitle(""); setCity(""); setImage(null);
      toast.success("Published");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const needsTitle = kind !== "post";

  return (
    <form onSubmit={submit} className="panel p-5 space-y-3" data-testid={`composer-${kind}`}>
      <div className="flex gap-3">
        <UserAvatar user={me} size={40} />
        <div className="flex-1 min-w-0 space-y-3">
          {needsTitle && <Input data-testid="composer-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === "question" ? "Your question" : "Title, e.g. 2 rope access techs needed in Gotland"} />}
          <Textarea data-testid="composer-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={PLACEHOLDER[kind]} />
          {image && (
            <div className="relative inline-block">
              <img src={image.preview} alt="" className="h-28 rounded object-cover" />
              <button type="button" onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-slate-900 text-white rounded-full p-1" data-testid="composer-image-remove"><X size={12} /></button>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(e.target.files[0])} data-testid="composer-image-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current.click()} disabled={busy} data-testid="composer-image-button"><ImagePlus size={15} />Photo</Button>
            {kind === "listing" && (
              <Select value={ltype} onValueChange={setLtype}>
                <SelectTrigger className="w-[170px] h-9" data-testid="composer-listing-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LISTING).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {kind === "listing" && <Input className="w-[150px] h-9" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" data-testid="composer-city" />}
            {!groupId && groups.length > 0 && (
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger className="w-[180px] h-9" data-testid="composer-group"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">No group</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button type="submit" className="ml-auto" disabled={busy || !text.trim() || (needsTitle && title.trim().length < 3)} data-testid="composer-submit">
              {busy && <Loader2 size={15} className="animate-spin" />}{kind === "question" ? "Ask" : "Publish"}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
